/**
 * Finger-number density: how many of a phrase's computed finger numbers are
 * drawn. Every fingered note carries a 0-100 "need" score (how much a reader
 * needs that number); a level is a threshold on it, and the fingering tables
 * say where each level's threshold lies.
 */
import type { Tables } from 'ErgoBot';

export const FINGERING_DISPLAY_LEVELS = ['none', 'minimal', 'all'] as const;

export type FingeringDisplay = (typeof FINGERING_DISPLAY_LEVELS)[number];

/** What a sight-reading scene needs to finger its phrases: the tables to finger
 *  them with and the density to draw their numbers at. `timed` is a testing
 *  aid: the scene then also says how long fingering each phrase took. */
export type LiveFingering = { tables: Tables; display: FingeringDisplay; timed?: boolean };

export const DEFAULT_FINGERING_DISPLAY: FingeringDisplay = 'minimal';

export const FINGERING_DISPLAY_LABELS: Record<FingeringDisplay, string> = {
  none: 'None',
  minimal: 'Minimal',
  all: 'All',
};

export const isFingeringDisplay = (value: unknown): value is FingeringDisplay =>
  typeof value === 'string' && (FINGERING_DISPLAY_LEVELS as readonly string[]).includes(value);

/**
 * The finger number to draw on a note when numbers are drawn from a need score
 * of `minimumNeed` up; undefined when none is. A number without a need score
 * was not computed in the app (it came with the phrase file) and is never drawn
 * alongside the computed ones.
 */
export const fingerNumberToDraw = (
  fingering: { value: string } | undefined,
  need: number | undefined,
  minimumNeed: number,
): string | undefined =>
  fingering !== undefined && need !== undefined && need >= minimumNeed
    ? fingering.value
    : undefined;
